import { Box, Button, Icons, Stack, Text } from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import { useField } from "react-final-form"
import { FieldGroup, FileInputField } from "./Form"
import { useEffect, useState, useRef } from "react"
import type { Crop } from "react-image-crop"
import ReactCrop, { makeAspectCrop, convertToPixelCrop } from "react-image-crop"

import "react-image-crop/dist/ReactCrop.css"

export type ImageCropInputFieldProps = React.ComponentProps<
	typeof FileInputField
> & {
	crop?:
		| {
				aspect?: number
				minWidth?: number
				maxWidth?: number
				minHeight?: number
				maxHeight?: number
				keepSelection?: boolean
				disabled?: boolean
				locked?: boolean
				circularCrop?: boolean
		  }
		| false
	// use this if you want to show preview of the image
	// if you use this, the field will hold a `File` named like the picked file (not a `Blob`)
	preview?: boolean
	initialImageUrl?: string | null
	altText?: string
}

// the preview box uses this shape when the crop has no fixed aspect ratio
const DEFAULT_PREVIEW_ASPECT = 3 / 2

export function ImageCropInputField({
	name,
	crop: initialCrop = {},
	preview = false,
	initialImageUrl,
	altText,
	...props
}: ImageCropInputFieldProps) {
	// holds the dataurl of image
	const [src, setSrc] = useState<string | null>(null)
	// the picked file, to name the cropped file after it
	const [pendingFile, setPendingFile] = useState<File | null>(null)
	const [cropError, setCropError] = useState<string | null>(null)
	// needs to reset the file input on crop cancellation
	const [fileResetKey, setFileResetKey] = useState<number>(0)
	const imgRef = useRef<HTMLImageElement | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [crop, setCrop] = useState<Crop>()
	// data url of the current file, kept with the file so a stale one is never shown
	const [previewData, setPreviewData] = useState<{
		file: File
		url: string
	} | null>(null)
	// `onBlur` marks the field touched, so the validation errors show up
	const {
		input: { value, onChange: changeFile, onBlur },
	} = useField<File | undefined | null>(name, {
		subscription: { value: true },
	})

	useEffect(() => {
		if (!preview || !(value instanceof File)) return undefined
		const file = value
		let isCancelled = false
		const reader = new FileReader()
		reader.addEventListener("load", () => {
			if (!isCancelled) setPreviewData({ file, url: reader.result as string })
		})
		reader.readAsDataURL(file)
		return () => {
			isCancelled = true
		}
	}, [preview, value])
	const previewUrl =
		value instanceof File && previewData?.file === value
			? previewData.url
			: null

	function readFile(file: File) {
		const reader = new FileReader()
		reader.addEventListener("load", () => setSrc(reader.result as string))
		reader.readAsDataURL(file)
	}
	function pickFile(file: File | null) {
		setCrop(undefined)
		setCropError(null)
		if (!file) {
			// with a preview, dismissing the picker keeps the previous image
			if (!preview) {
				changeFile(null)
				setSrc(null)
			}
			return
		}
		if (!preview || initialCrop === false) {
			changeFile(file)
			if (preview) onBlur()
		}
		if (initialCrop !== false) {
			setPendingFile(file)
			readFile(file)
		}
	}
	function cancelCrop() {
		setSrc(null)
		setCrop(undefined)
		setPendingFile(null)
		setCropError(null)
		if (preview) {
			if (fileInputRef.current) fileInputRef.current.value = ""
		} else {
			setFileResetKey((key) => key + 1)
			changeFile(null)
		}
	}
	async function applyCrop() {
		if (!imgRef.current || !crop) {
			cancelCrop()
			return
		}
		let blob: Blob
		try {
			blob = await getCroppedImg(imgRef.current, crop)
		} catch (e) {
			// the dialog stays open, so the crop can be adjusted and tried again
			setCropError(
				(e as Error).message || "Please try again with another selection."
			)
			return
		}
		const file =
			preview && pendingFile
				? new File([blob], pendingFile.name, { type: blob.type })
				: blob
		changeFile(props.multiple ? [file] : file)
		if (preview) onBlur()
		setCropError(null)
		setSrc(null)
		setCrop(undefined)
		setPendingFile(null)
	}

	const displayImageUrl = previewUrl || initialImageUrl
	const previewAspect =
		(initialCrop && initialCrop.aspect) || DEFAULT_PREVIEW_ASPECT
	const previewSize = {
		width: "200px",
		height: `${Math.round(200 / previewAspect)}px`,
	}
	return (
		<>
			{preview ? (
				<FieldGroup
					name={name}
					label={props.label}
					secondaryLabel={props.secondaryLabel}
					help={props.help}
					hideErrors={props.hideErrors}
					required={props.required}
				>
					{() => (
						<Stack gap="2" alignItems="start" style={{ maxWidth: "200px" }}>
							{displayImageUrl ? (
								<Box
									rounded="lg"
									overflow="hidden"
									style={previewSize}
									alignItems="center"
								>
									<img
										src={displayImageUrl}
										alt={altText || (props.label ? String(props.label) : "")}
										loading="lazy"
										style={{
											objectFit: "cover",
											textAlign: "center",
										}}
									/>
								</Box>
							) : (
								<Box
									style={previewSize}
									display="flex"
									justifyContent="center"
									alignItems="center"
									bgColor="inset"
									rounded="lg"
								>
									<Icons.Photograph size="16" />
								</Box>
							)}
							<input
								ref={fileInputRef}
								type="file"
								accept={props.accept}
								disabled={props.disabled}
								style={{ display: "none" }}
								onClick={(e) => {
									// lets the same file be picked again
									;(e.target as HTMLInputElement).value = ""
								}}
								onChange={(e) => pickFile(e.target.files?.[0] || null)}
							/>
							<Button
								level="secondary"
								type="button"
								disabled={props.disabled}
								onClick={() => {
									fileInputRef.current?.click()
								}}
							>
								{displayImageUrl ? "Update Image" : "Add Image"}
							</Button>
						</Stack>
					)}
				</FieldGroup>
			) : (
				<FileInputField
					name={name}
					key={fileResetKey}
					{...props}
					onChange={(files) => pickFile(files?.[0] || null)}
				/>
			)}
			{initialCrop !== false ? (
				<Dialog
					open={Boolean(src)}
					sm={initialCrop?.aspect === 1}
					onClose={cancelCrop}
					title="Crop Image"
				>
					<Dialog.Body>
						{!src ? (
							<Box>Please select a valid image file.</Box>
						) : (
							<ReactCrop
								aspect={initialCrop.aspect}
								crop={crop}
								ruleOfThirds
								onChange={setCrop}
								minWidth={initialCrop.minWidth}
								maxWidth={initialCrop.maxWidth}
								minHeight={initialCrop.minHeight}
								maxHeight={initialCrop.maxHeight}
								keepSelection={initialCrop.keepSelection}
								disabled={initialCrop.disabled}
								locked={initialCrop.locked}
								circularCrop={initialCrop.circularCrop}
							>
								<img
									src={src}
									ref={imgRef}
									alt="Croppable"
									onLoad={(e) => {
										if (initialCrop.aspect) {
											const { width, height } = e.currentTarget
											setCrop(
												aspectPixelCrop(width, height, initialCrop.aspect)
											)
										}
									}}
								/>
							</ReactCrop>
						)}
						{cropError ? (
							<Text color="danger" fontSize="sm">
								{cropError}
							</Text>
						) : null}
					</Dialog.Body>
					<Dialog.Footer>
						<Button level="primary" type="button" onClick={applyCrop}>
							Done
						</Button>
						<Button type="button" onClick={cancelCrop}>
							Cancel
						</Button>
					</Dialog.Footer>
				</Dialog>
			) : null}
		</>
	)
}

export function getCroppedImg(
	image: HTMLImageElement,
	crop: Crop,
	scale = 1,
	rotate = 0
): Promise<Blob> {
	const canvas = document.createElement("canvas")
	const ctx = canvas.getContext("2d")
	if (!ctx) {
		throw new Error("Unable to draw image to canvas")
	}
	const scaleX = image.naturalWidth / image.width
	const scaleY = image.naturalHeight / image.height
	// Output at the crop's native source resolution only. Do not also multiply by
	// window.devicePixelRatio here: that describes the *screen's* pixel density, not
	// the source image's, so on Retina/HiDPI displays it inflated the canvas 4-9x
	// with no real added detail - producing a much bigger re-encoded file (especially
	// for lossless PNG) and tripping the 1MB upload size check on small source images.
	canvas.width = Math.floor(crop.width * scaleX)
	canvas.height = Math.floor(crop.height * scaleY)

	ctx.imageSmoothingQuality = "high"

	const TO_RADIANS = Math.PI / 180

	const cropX = crop.x * scaleX
	const cropY = crop.y * scaleY
	const rotateRads = rotate * TO_RADIANS
	const centerX = image.naturalWidth / 2
	const centerY = image.naturalHeight / 2
	ctx.save()
	// 5) Move the crop origin to the canvas origin (0,0)
	ctx.translate(-cropX, -cropY)
	// 4) Move the origin to the center of the original position
	ctx.translate(centerX, centerY)
	// 3) Rotate around the origin
	ctx.rotate(rotateRads)
	// 2) Scale the image
	ctx.scale(scale, scale)
	// 1) Move the center of the image to the origin (0,0)
	ctx.translate(-centerX, -centerY)
	ctx.drawImage(
		image,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight
	)

	ctx.restore()

	const src = image.src

	let fileType = "image/jpeg"
	if (src.indexOf(";base64") !== -1) {
		fileType = src.split(";base64")[0]
		fileType = fileType.replace("data:", "")
	}

	if (!HTMLCanvasElement.prototype.toBlob) {
		// polyfill the canvas.toBlob
		Object.defineProperty(HTMLCanvasElement.prototype, "toBlob", {
			value: function (callback: BlobCallback, type: string, quality?: number) {
				const binStr = atob(this.toDataURL(type, quality).split(",")[1]),
					len = binStr.length,
					arr = new Uint8Array(len)
				for (let i = 0; i < len; i++) {
					arr[i] = binStr.charCodeAt(i)
				}
				callback(new Blob([arr], { type: type || "image/png" }))
			},
		})
	}

	// As a blob
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				return reject("Unable to convert canvas to image.")
			}
			resolve(blob)
		}, fileType)
	})
}

function aspectPixelCrop(
	mediaWidth: number,
	mediaHeight: number,
	aspect: number
) {
	return convertToPixelCrop(
		makeAspectCrop(
			{
				unit: "%",
				width: 90,
				x: 0,
				y: 0,
			},
			aspect,
			mediaWidth,
			mediaHeight
		),
		mediaWidth,
		mediaHeight
	)
}
